import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Box, Typography, Pagination, useMediaQuery, useTheme, Fade, Skeleton, Select, MenuItem, } from '@mui/material';
import BookingCard from './BookingCard';
import { useTranslation } from 'react-i18next';
import { useWindowWidth } from '@react-hook/window-size';
export default function ListOfBoats(props) {
    const { boats = [], customerId = '', isLoading = false } = props;
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.only('xs'));
    const isSm = useMediaQuery(theme.breakpoints.only('sm'));
    const handlePageChange = (_, value) => {
        setCurrentPage(value);
    };
    const handleCategoryChange = (event) => {
        setSelectedCategory(event.target.value);
        setCurrentPage(1);
    };
    const windowWidth = useWindowWidth();
    const totalPadding = 16 * 2; // 16px left + right padding
    const availableWidth = windowWidth - totalPadding;
    const bookingCardWidth = 300; // Adjusted to fit more cards
    const bookingSpacerWidth = 16; // Gap between cards
    // Calculate how many columns fit in the grid
    const columns = Math.max(Math.floor((availableWidth + bookingSpacerWidth) / (bookingCardWidth + bookingSpacerWidth)), 1);
    // Ensure at least 1 column is shown
    const boatsPerPage = columns * 2; // Assuming 2 rows of cards per page
    // Extract unique categories
    const uniqueCategories = Array.from(new Set(boats.map(({ Category }) => Category).filter((cat) => cat)));
    // Filter by category
    const filteredBoats = boats.filter(({ Category }) => selectedCategory === 'all' || Category === selectedCategory);
    // Pagination logic
    const indexOfLastBoat = currentPage * boatsPerPage;
    const indexOfFirstBoat = indexOfLastBoat - boatsPerPage;
    const currentBoats = filteredBoats.slice(indexOfFirstBoat, indexOfLastBoat);
    // Skeleton placeholders
    const skeletons = Array.from(new Array(boatsPerPage));
    return (_jsxs(Box, { sx: {
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'var(--background-color)',
        }, children: [_jsx(Fade, { in: true, timeout: 500, children: _jsxs(Box, { sx: {
                        flexShrink: 0,
                        textAlign: 'center',
                        mb: 2,
                        pt: 4,
                    }, children: [_jsx(Typography, { variant: "h3", sx: {
                                fontWeight: 'bold',
                                marginBottom: '20px',
                                color: 'var(--secondaryFgColor)!important',
                            }, children: t('listOfBoats.exploreFleet') }), _jsxs(Select, { value: selectedCategory, onChange: handleCategoryChange, displayEmpty: true, sx: {
                                textTransform: 'none',
                                fontWeight: 'bold',
                                color: 'var(--primaryFgColor)!important',
                                border: '1px solid var(--primaryFgColor)',
                                borderRadius: 1,
                                '& .MuiSelect-icon': {
                                    color: 'var(--primaryFgColor)!important',
                                },
                                '&:hover': {
                                    backgroundColor: 'var(--primaryBgColor)!important',
                                    opacity: 0.9,
                                },
                            }, children: [_jsx(MenuItem, { value: "all", children: t('listOfBoats.allCategories') }), uniqueCategories.map((category) => (_jsx(MenuItem, { value: category, children: category.charAt(0).toUpperCase() + category.slice(1) }, category)))] })] }) }), _jsx(Box, { display: "grid", padding: 2, gap: 2, gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", children: isLoading ? (skeletons.map((_, _index) => (_jsx(Skeleton, { variant: "rectangular", height: 220, sx: { borderRadius: 2, bgcolor: '#ddd' } })))) : currentBoats.length > 0 ? (currentBoats.map(({ BoatName, Images, Description, BoatID }) => (_jsx(BookingCard, { title: BoatName, images: Images, description: Description, boatId: BoatID, customerId: customerId }, BoatID)))) : (_jsx(Typography, { variant: "h6", align: "center", color: "var(--secondaryFgColor)!important", sx: { marginTop: 4 } })) }), _jsx(Box, { sx: {
                    flexShrink: 0,
                    p: 2,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 2,
                    backgroundColor: 'var(--background-color)',
                }, children: !isLoading && filteredBoats.length > boatsPerPage && (_jsx(Pagination, { count: Math.ceil(filteredBoats.length / boatsPerPage), page: currentPage, onChange: handlePageChange, shape: "rounded", size: isSm || isXs ? 'small' : 'medium', sx: {
                        '& .MuiPaginationItem-root': {
                            color: 'var(--secondaryFgColor)!important',
                            borderColor: 'var(--secondaryFgColor)!important',
                        },
                        '& .Mui-selected': {
                            backgroundColor: 'var(--primaryFgColor)!important',
                            color: 'var(--primaryBgColor)!important',
                        },
                    } })) })] }));
}
